/**
 * Florin onboarding — the full real-user journey (BRIEF §4, §26):
 *
 *   welcome → get a card (email → OTP → terms → Sumsub KYC → source of funds
 *   → phone → Safe deploy → virtual card) → pick stock/rounding → done
 *
 * plus two alternate paths: "I already have a Gnosis Pay card" (paste Safe)
 * and demo mode. Every step is resumable: state is derived from
 * GET /user + /user/terms, so closing the PWA mid-KYC and reopening lands
 * the user exactly where they left off.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import * as gp from "../lib/gnosispay/api";
import { ROUNDING_OPTIONS, type RoundingIncrement } from "../lib/roundup";
import { formatPence } from "../lib/money";
import {
  exportWalletKey,
  importWallet,
  maintainGasAccount,
  resetApp,
  topUpReserve,
  updateSettings,
} from "../lib/store";
import { loadOrCreateWallet, walletFromKey } from "../lib/wallet";
import { TapToPay } from "./TapToPay";
import type { FiatCurrency, Hex } from "../lib/types";

const STOCKS = ["NVDA", "AAPL", "TSLA", "SPY"];

function safeCurrency(config: gp.GpSafeConfig): FiatCurrency {
  const denomination = `${config.fiatSymbol ?? ""} ${config.tokenSymbol ?? ""}`.toUpperCase();
  if (denomination.includes("EUR")) return "EUR";
  if (denomination.includes("USD")) return "USD";
  return "GBP";
}

type Step =
  | "welcome"
  | "path"
  | "existing"
  | "restore"
  | "devsettings"
  | "email"
  | "otp"
  | "terms"
  | "kyc"
  | "sof"
  | "phone"
  | "safe"
  | "card"
  | "invest";

const TERM_LABELS: Record<gp.GpTermsEntry["type"], string> = {
  "general-tos": "General Terms of Service",
  "card-monavate-tos": "Card Terms (Monavate)",
  "cashback-tos": "Cashback Terms",
  "privacy-policy": "Privacy Policy",
};

export function Onboarding() {
  const [step, setStep] = useState<Step>("welcome");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [terms, setTerms] = useState<gp.GpTermsEntry[]>([]);
  const [kycUrl, setKycUrl] = useState("");
  const [kycStatus, setKycStatus] = useState<gp.KycStatus>("notStarted");
  const [sofQuestions, setSofQuestions] = useState<gp.SofQuestion[]>([]);
  const [sofAnswers, setSofAnswers] = useState<Record<string, string>>({});
  const [phone, setPhone] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);
  const [safeAddress, setSafeAddress] = useState<string | null>(null);
  const [currency, setCurrency] = useState<FiatCurrency>("GBP");
  const [cardLast4, setCardLast4] = useState<string | null>(null);
  const [existingSafe, setExistingSafe] = useState("");
  const [importKey, setImportKey] = useState("");
  const [showKey, setShowKey] = useState(false);

  const [stock, setStock] = useState("NVDA");
  const [increment, setIncrement] = useState<RoundingIncrement>(100);
  const retriedRef = useRef(false);
  const settingsReturnRef = useRef<Step>("welcome");

  const openDevSettings = (from: Step) => {
    settingsReturnRef.current = from;
    setShowKey(false);
    setStep("devsettings");
  };

  /** Derive the next step from server-side state (makes the flow resumable). */
  const advance = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const user = await gp.getUser();
      retriedRef.current = false; // session proven good — re-arm the silent re-auth
      const t = await gp.getTerms();
      setTerms(t);
      setKycStatus(user.kycStatus);
      if (t.some(gp.termsNeedAccepting)) return setStep("terms");
      if (["notStarted", "documentsRequested", "requiresAction", "resubmissionRequested", "rejected"].includes(user.kycStatus)) {
        setKycUrl(await gp.getKycUrl().catch(() => ""));
        return setStep("kyc");
      }
      if (!user.isSourceOfFundsAnswered) {
        setSofQuestions(await gp.getSourceOfFundsQuestions().catch(() => []));
        return setStep("sof");
      }
      if (!user.isPhoneValidated) return setStep("phone");
      const safe = await gp.getSafeConfig().catch(() => null);
      if (!safe?.isDeployed || !safe.address) return setStep("safe");
      setSafeAddress(safe.address);
      setCurrency(safeCurrency(safe));
      if (user.kycStatus !== "approved") {
        setKycUrl(await gp.getKycUrl().catch(() => ""));
        return setStep("kyc"); // submitted but still in review — card needs approval
      }
      if (user.cards.length === 0) return setStep("card");
      setCardLast4(user.cards[0].lastFourDigits);
      return setStep("invest");
    } catch (err) {
      if (err instanceof gp.GpApiError && err.isAuth) {
        // Session expired — one silent SIWE re-auth, else back to email.
        const wallet = await loadOrCreateWallet();
        const ok = await gp
          .siweLogin(wallet)
          .then(() => true)
          .catch(() => false);
        if (ok && !retriedRef.current) {
          retriedRef.current = true;
          return advance();
        }
        retriedRef.current = false;
        return setStep("email");
      }
      if (err instanceof gp.GpApiError && err.status === 404) {
        // Wallet session exists but no Gnosis Pay user is attached yet —
        // the signup was interrupted before the email step. Resume there.
        return setStep("email");
      }
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setBusy(false);
    }
  }, []);

  // Resume an in-progress signup when the PWA reopens.
  useEffect(() => {
    void (async () => {
      if (await gp.hasSession()) {
        setStep("path");
        await advance();
      }
    })();
  }, [advance]);

  // Poll KYC status while on the KYC step. Sumsub reviews take minutes, so
  // 30s is plenty — 5s polling hammered Gnosis Pay's WAF-guarded API for no
  // benefit (and there's a manual "check now" button anyway).
  useEffect(() => {
    if (step !== "kyc") return;
    const t = setInterval(async () => {
      try {
        const user = await gp.getUser();
        setKycStatus(user.kycStatus);
        if (user.kycStatus === "approved") {
          clearInterval(t);
          await advance();
        }
      } catch {
        // transient — keep polling
      }
    }, 30_000);
    return () => clearInterval(t);
  }, [step, advance]);

  const run = (fn: () => Promise<void>) => {
    setBusy(true);
    setError("");
    fn()
      .catch((err) => setError(String(err instanceof Error ? err.message : err)))
      .finally(() => setBusy(false));
  };

  const finish = async (demo: boolean, watchedSafe?: string) => {
    await updateSettings({
      onboarded: true,
      demoMode: demo,
      safeAddress: (watchedSafe ?? safeAddress ?? undefined) as Hex | undefined,
      stockSymbol: stock,
      incrementPence: increment,
      cardLast4: cardLast4 ?? undefined,
      currency: demo ? "GBP" : currency,
    });
    if (demo) await topUpReserve(1000); // £10 starter demo reserve
    else void maintainGasAccount();
  };

  const input = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
    <input type="text" spellCheck={false} autoCapitalize="none" {...props} />
  );

  /**
   * Gnosis Pay accounts belong to the WALLET (the email is an attribute), so
   * "already registered" means the email is attached to a wallet on another
   * device — usually earlier testing. Point at the restore path instead of
   * leaving a dead end.
   */
  const explainSignupError = (err: unknown): never => {
    const msg = String(err instanceof Error ? err.message : err).toLowerCase();
    if (msg.includes("already registered") || msg.includes("already associated")) {
      throw new Error(
        "This email already has a Gnosis Pay account — created on whichever device you first " +
          "signed up from. Accounts belong to that device's wallet, not the email. Go back and " +
          "choose “Continue from another device” to restore that wallet's key here " +
          "(Settings → Reveal private key on the original device), or use a different email.",
      );
    }
    throw err;
  };

  return (
    <div className="screen" style={{ display: "flex", flexDirection: "column" }}>
      {error && <div className="notice">{error}</div>}

      {step === "welcome" && (
        <>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", textAlign: "center" }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🪙</div>
            <h1>Florin</h1>
            <p className="sub" style={{ fontSize: 16 }}>
              Pay with your card as normal. Florin rounds each purchase up to the nearest pound and
              invests the change into real stocks — automatically.
            </p>
            <div className="card" style={{ textAlign: "left" }}>
              <div className="kv"><span className="k">Coffee</span><span className="v">£3.42</span></div>
              <div className="kv"><span className="k">Rounded to</span><span className="v">£4.00</span></div>
              <div className="kv">
                <span className="k">Invested in NVDA</span>
                <span className="v" style={{ color: "var(--accent)", fontWeight: 700 }}>£0.58</span>
              </div>
            </div>
          </div>
          <button className="btn-primary" onClick={() => setStep("path")}>Get started</button>
          <button className="btn-secondary" onClick={() => openDevSettings("welcome")}>
            Device settings
          </button>
        </>
      )}

      {step === "devsettings" && (
        <>
          <h1>Device settings</h1>
          <h2>Wallet key</h2>
          <p className="sub">
            This key IS your account — paste it into “Continue from another device” elsewhere to
            move it. Anyone holding it controls the account, so keep it private.
          </p>
          {!showKey ? (
            <button className="btn-secondary" onClick={() => setShowKey(true)}>
              Reveal wallet key
            </button>
          ) : (
            <div className="card">
              <code style={{ fontSize: 11, wordBreak: "break-all" }}>{exportWalletKey()}</code>
              <div style={{ marginTop: 10 }}>
                <button className="btn-secondary" onClick={() => setShowKey(false)}>
                  Hide
                </button>
              </div>
            </div>
          )}
          <h2>Start over</h2>
          <p className="sub">
            Wipes everything on THIS device — wallet key, session, history — and restarts with a
            fresh wallet. Note: emails already registered with Gnosis Pay stay registered on their
            side; to reuse one straight away, sign up with a +tag variant (you+florin@gmail.com
            reaches the same inbox).
          </p>
          <button
            className="btn-danger"
            onClick={() => {
              if (
                confirm(
                  "Wipe all local data, wallet key included? If this wallet owns a Gnosis Pay account or any funds, they are unrecoverable unless you saved the key.",
                )
              )
                void resetApp();
            }}
          >
            Clear all data on this device
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn-secondary" onClick={() => setStep(settingsReturnRef.current)}>
            Back
          </button>
        </>
      )}

      {step === "path" && (
        <>
          <h1>Your card</h1>
          <p className="sub">Florin issues you a free virtual Visa, powered by Gnosis Pay.</p>
          <div className="option-list">
            <button
              className="option selected"
              disabled={busy}
              onClick={() =>
                // The wallet IS the account: sign in with it first. Email is
                // only needed when no account is attached to this wallet yet
                // (advance() routes there on 404).
                run(async () => {
                  const wallet = await loadOrCreateWallet();
                  const ok = await gp
                    .siweLogin(wallet)
                    .then(() => true)
                    .catch(() => false);
                  if (ok) await advance();
                  else setStep("email");
                })
              }
            >
              <span>
                Get your free virtual card
                <span className="option-sub">Sign up, verify your identity, start spending — ~10 minutes</span>
              </span>
            </button>
            <button className="option" onClick={() => setStep("existing")}>
              <span>
                I already have a Gnosis Pay card
                <span className="option-sub">Watch my existing Safe for purchases</span>
              </span>
            </button>
            <button className="option" onClick={() => setStep("restore")}>
              <span>
                Continue from another device
                <span className="option-sub">Restore the wallet key from where you first signed up</span>
              </span>
            </button>
            <button
              className="option"
              onClick={() => {
                void finish(true);
              }}
            >
              <span>
                Try the demo first
                <span className="option-sub">Simulated purchases — see the whole loop instantly</span>
              </span>
            </button>
          </div>
          {busy && <p className="sub" style={{ marginTop: 12 }}>Checking your account…</p>}
          <div style={{ flex: 1 }} />
          <button className="btn-secondary" onClick={() => openDevSettings("path")}>
            Device settings
          </button>
          <button className="btn-secondary" onClick={() => setStep("welcome")}>
            Back
          </button>
        </>
      )}

      {step === "existing" && (
        <>
          <h1>Watch your Safe</h1>
          <p className="sub">Paste your Gnosis Pay Safe address — Florin watches it read-only.</p>
          {input({
            placeholder: "0x… your Gnosis Pay Safe address",
            value: existingSafe,
            onChange: (e) => setExistingSafe((e.target as HTMLInputElement).value),
          })}
          <div style={{ flex: 1 }} />
          <button
            className="btn-primary"
            disabled={!/^0x[0-9a-fA-F]{40}$/.test(existingSafe.trim())}
            onClick={() => setStep("invest")}
          >
            Continue
          </button>
          <button className="btn-secondary" onClick={() => setStep("path")}>
            Back
          </button>
        </>
      )}

      {step === "restore" && (
        <>
          <h1>Continue from another device</h1>
          <p className="sub">
            On the device where you first signed up, open Settings → “Reveal private key” and paste
            the key here. Your account, KYC progress and card all follow the wallet.
          </p>
          {input({
            placeholder: "0x… wallet private key",
            value: importKey,
            onChange: (e) => setImportKey((e.target as HTMLInputElement).value),
          })}
          <div style={{ flex: 1 }} />
          <button
            className="btn-primary"
            disabled={busy || !/^0x[0-9a-fA-F]{64}$/.test(importKey.trim())}
            onClick={() =>
              run(async () => {
                // Verify the key actually owns an account BEFORE adopting it,
                // so a key from the wrong browser fails loudly, not silently.
                const temp = walletFromKey(importKey);
                await gp.siweLogin(temp);
                await gp.getUser().catch((err) => {
                  void gp.signOut();
                  if (err instanceof gp.GpApiError && (err.status === 404 || err.isAuth)) {
                    throw new Error(
                      "This key signs in, but no Gnosis Pay account is attached to it. " +
                        "Export the key from the exact browser AND address you signed up on — " +
                        "localhost and florin.buzz keep separate wallets, and so does every " +
                        "browser profile. On the right one, “Get your free virtual card” will " +
                        "jump straight to where you left off.",
                    );
                  }
                  throw err;
                });
                await importWallet(importKey);
                setImportKey("");
                await advance(); // resumes exactly where the account left off
              })
            }
          >
            {busy ? "Restoring…" : "Restore & continue"}
          </button>
          <button className="btn-secondary" disabled={busy} onClick={() => setStep("path")}>
            Back
          </button>
        </>
      )}

      {step === "email" && (
        <>
          <h1>Create your account</h1>
          <p className="sub">We'll email you a 6-digit code to confirm it's you.</p>
          <p className="sub">
            Email already registered from earlier testing? A +tag makes it fresh:
            you+florin@gmail.com delivers to the same inbox.
          </p>
          {input({
            placeholder: "you@example.com",
            value: email,
            inputMode: "email",
            onChange: (e) => setEmail((e.target as HTMLInputElement).value),
          })}
          <div style={{ flex: 1 }} />
          <button
            className="btn-primary"
            disabled={busy || !/^\S+@\S+\.\S+$/.test(email.trim())}
            onClick={() =>
              run(async () => {
                // Gnosis Pay auth is wallet-first: establish the SIWE session
                // with Florin's wallet, THEN attach the email.
                const wallet = await loadOrCreateWallet();
                await gp.siweLogin(wallet).catch((err) => {
                  const msg = String(err instanceof Error ? err.message : err);
                  if (msg.toLowerCase().includes("domain")) {
                    throw new Error(
                      "Gnosis Pay hasn't enabled this domain for our partner account yet. " +
                        "Check the partner dashboard for a publish/production step, or contact their support.",
                    );
                  }
                  throw err;
                });
                await gp.requestEmailOtp(email.trim()).catch(explainSignupError);
                setStep("otp");
              })
            }
          >
            {busy ? "Sending…" : "Send code"}
          </button>
          <button className="btn-secondary" disabled={busy} onClick={() => setStep("path")}>
            Back
          </button>
        </>
      )}

      {step === "otp" && (
        <>
          <h1>Check your email</h1>
          <p className="sub">Enter the 6-digit code we sent to {email.trim()}.</p>
          <p className="sub">
            Nothing after a minute? Check spam — or if this email was used before, no code is
            sent: use “Continue without it” below.
          </p>
          {input({
            placeholder: "123456",
            value: otp,
            inputMode: "numeric",
            maxLength: 6,
            onChange: (e) => setOtp((e.target as HTMLInputElement).value.replace(/\D/g, "")),
          })}
          <div style={{ flex: 1 }} />
          <button
            className="btn-primary"
            disabled={busy || otp.length !== 6}
            onClick={() =>
              run(async () => {
                await gp.signup(email.trim(), otp).catch(explainSignupError);
                const wallet = await loadOrCreateWallet();
                await gp.ensureSignInWallet(wallet).catch(() => undefined);
                await advance();
              })
            }
          >
            {busy ? "Verifying…" : "Verify"}
          </button>
          <button
            className="btn-secondary"
            disabled={busy}
            onClick={() =>
              // Gnosis Pay's own signup doesn't require the code (otp is
              // optional server-side) — don't let a lost email block signup.
              run(async () => {
                await gp.signup(email.trim()).catch(explainSignupError);
                const wallet = await loadOrCreateWallet();
                await gp.ensureSignInWallet(wallet).catch(() => undefined);
                await advance();
              })
            }
          >
            No code? Continue without it
          </button>
          <button className="btn-secondary" disabled={busy} onClick={() => setStep("email")}>
            Use a different email
          </button>
        </>
      )}

      {step === "terms" && (
        <>
          <h1>The legal bit</h1>
          <p className="sub">Gnosis Pay (the regulated card issuer) needs you to accept its terms.</p>
          <div className="card">
            {terms.map((t) => (
              <div className="kv" key={t.type}>
                <span className="k">
                  <a
                    href="https://gnosispay.com/terms"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "var(--text)" }}
                  >
                    {TERM_LABELS[t.type] ?? t.type}
                  </a>
                </span>
                <span className="v">{!gp.termsNeedAccepting(t) ? "✓" : `v${t.currentVersion}`}</span>
              </div>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          <button
            className="btn-primary"
            disabled={busy}
            onClick={() =>
              run(async () => {
                for (const t of terms.filter(gp.termsNeedAccepting)) {
                  await gp.acceptTerms(t.type, t.currentVersion);
                }
                await advance();
              })
            }
          >
            {busy ? "Accepting…" : "Accept all"}
          </button>
          <button className="btn-secondary" disabled={busy} onClick={() => setStep("path")}>
            Back
          </button>
        </>
      )}

      {step === "kyc" && (
        <>
          <h1>Verify your identity</h1>
          {["pending", "processing"].includes(kycStatus) ? (
            <>
              <p className="sub">
                Your documents are in review — usually a few minutes. This screen updates itself,
                and you can close the app and come back.
              </p>
              <div className="card" style={{ textAlign: "center", padding: 28 }}>
                <div style={{ fontSize: 40, marginBottom: 8 }}>⏳</div>
                <span className="pill">{kycStatus}</span>
              </div>
            </>
          ) : (
            <>
              <p className="sub">
                {kycStatus === "resubmissionRequested" || kycStatus === "rejected"
                  ? "Verification needs another attempt — open the secure form below."
                  : "A quick ID check, handled by Sumsub in a secure window. Have your passport or driving licence ready."}
              </p>
              {kycUrl ? (
                <a href={kycUrl} target="_blank" rel="noreferrer">
                  <button className="btn-primary">Open identity check</button>
                </a>
              ) : (
                <button className="btn-secondary" onClick={() => run(async () => setKycUrl(await gp.getKycUrl()))}>
                  Load identity check
                </button>
              )}
              <p className="sub" style={{ marginTop: 10 }}>
                When you finish in the other tab, come back here — this screen advances
                automatically once you're approved.
              </p>
            </>
          )}
          <div style={{ flex: 1 }} />
          <button className="btn-secondary" disabled={busy} onClick={() => void advance()}>
            {busy ? "Checking…" : "I've completed it — check now"}
          </button>
          <button className="btn-secondary" disabled={busy} onClick={() => setStep("path")}>
            Back
          </button>
        </>
      )}

      {step === "sof" && (
        <>
          <h1>A few questions</h1>
          <p className="sub">Anti-money-laundering rules require these. Thirty seconds, promise.</p>
          {sofQuestions.map((q) => (
            <div key={q.question} style={{ marginBottom: 14 }}>
              <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{q.question}</p>
              <div className="option-list">
                {q.answers.map((a) => (
                  <button
                    key={a}
                    className={`option ${sofAnswers[q.question] === a ? "selected" : ""}`}
                    onClick={() => setSofAnswers({ ...sofAnswers, [q.question]: a })}
                  >
                    <span>{a}</span>
                    {sofAnswers[q.question] === a && <span className="tick">✓</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div style={{ flex: 1 }} />
          <button
            className="btn-primary"
            disabled={busy || sofQuestions.some((q) => !sofAnswers[q.question])}
            onClick={() =>
              run(async () => {
                await gp.submitSourceOfFunds(
                  sofQuestions.map((q) => ({ question: q.question, answer: sofAnswers[q.question] })),
                );
                await advance();
              })
            }
          >
            {busy ? "Submitting…" : "Continue"}
          </button>
          <button className="btn-secondary" disabled={busy} onClick={() => setStep("path")}>
            Back
          </button>
        </>
      )}

      {step === "phone" && (
        <>
          <h1>Verify your phone</h1>
          <p className="sub">Your phone number doubles as the card's security check for online payments.</p>
          {input({
            placeholder: "+44 7700 900000",
            value: phone,
            inputMode: "tel",
            onChange: (e) => setPhone((e.target as HTMLInputElement).value),
          })}
          {phoneCodeSent && (
            <div style={{ marginTop: 10 }}>
              {input({
                placeholder: "Code from SMS",
                value: phoneCode,
                inputMode: "numeric",
                onChange: (e) => setPhoneCode((e.target as HTMLInputElement).value.replace(/\D/g, "")),
              })}
            </div>
          )}
          <div style={{ flex: 1 }} />
          {!phoneCodeSent ? (
            <button
              className="btn-primary"
              disabled={busy || phone.trim().length < 8}
              onClick={() =>
                run(async () => {
                  await gp.requestPhoneOtp(phone.trim().replace(/\s/g, ""));
                  setPhoneCodeSent(true);
                })
              }
            >
              {busy ? "Sending…" : "Send SMS code"}
            </button>
          ) : (
            <button
              className="btn-primary"
              disabled={busy || phoneCode.length < 4}
              onClick={() =>
                run(async () => {
                  await gp.verifyPhone(phoneCode);
                  await advance();
                })
              }
            >
              {busy ? "Verifying…" : "Verify"}
            </button>
          )}
          <button className="btn-secondary" disabled={busy} onClick={() => setStep("path")}>
            Back
          </button>
        </>
      )}

      {step === "safe" && (
        <>
          <h1>Creating your account Safe</h1>
          <p className="sub">
            Your money lives in a Safe smart account that only you control. Deploying it is free and
            takes about a minute.
          </p>
          <div className="card" style={{ textAlign: "center", padding: 28 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>{busy ? "⏳" : "🔐"}</div>
            {safeAddress && <span className="pill ok">deployed</span>}
          </div>
          <div style={{ flex: 1 }} />
          <button
            className="btn-primary"
            disabled={busy}
            onClick={() =>
              run(async () => {
                const status = await gp.getSafeDeployStatus();
                if (status === "not_deployed" || status === "failed") await gp.deploySafe();
                // poll until deployed
                for (let i = 0; i < 30; i++) {
                  await new Promise((r) => setTimeout(r, 4_000));
                  if ((await gp.getSafeDeployStatus()) === "ok") break;
                }
                await advance();
              })
            }
          >
            {busy ? "Deploying…" : "Create my Safe"}
          </button>
          <button className="btn-secondary" disabled={busy} onClick={() => setStep("path")}>
            Back
          </button>
        </>
      )}

      {step === "card" && (
        <>
          <h1>Your virtual card</h1>
          <p className="sub">Free, instant, and ready for online checkout, Apple Pay and Google Pay.</p>
          <div style={{ flex: 1 }} />
          <button
            className="btn-primary"
            disabled={busy}
            onClick={() =>
              run(async () => {
                await gp.createVirtualCard();
                const cards = await gp.getCards();
                if (cards.length > 0) setCardLast4(cards[0].lastFourDigits);
                setStep("invest");
              })
            }
          >
            {busy ? "Creating…" : "Create my card"}
          </button>
          <button className="btn-secondary" disabled={busy} onClick={() => setStep("path")}>
            Back
          </button>
        </>
      )}

      {step === "invest" && (
        <>
          <h1>What to invest in</h1>
          {cardLast4 && (
            <div className="card" style={{ textAlign: "center" }}>
              <div style={{ fontSize: 28 }}>💳</div>
              <p style={{ fontWeight: 700 }}>Card •••• {cardLast4} is live</p>
            </div>
          )}
          {cardLast4 && <TapToPay compact />}
          <p className="sub">Every round-up buys a slice of one stock. Change it anytime.</p>
          <div className="option-list">
            {STOCKS.map((sym) => (
              <button key={sym} className={`option ${stock === sym ? "selected" : ""}`} onClick={() => setStock(sym)}>
                <span>{sym}</span>
                {stock === sym && <span className="tick">✓</span>}
              </button>
            ))}
          </div>
          <h2>Round up to</h2>
          <div className="option-list">
            {ROUNDING_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`option ${increment === opt.value ? "selected" : ""}`}
                onClick={() => setIncrement(opt.value)}
              >
                <span>
                  {currency === "GBP"
                    ? opt.label
                    : `Nearest ${formatPence(opt.value, currency).replace(/\.00$/, "")}`}
                </span>
                {increment === opt.value && <span className="tick">✓</span>}
              </button>
            ))}
          </div>
          <p className="sub" style={{ marginTop: 12 }}>
            Last step after this: add money in Settings → “Money in” — a normal bank transfer to
            your personal account number, then move some into the round-up reserve.
          </p>
          <div style={{ flex: 1 }} />
          <button
            className="btn-primary"
            onClick={() => void finish(false, existingSafe.trim() || undefined)}
          >
            Start investing my change
          </button>
          <button className="btn-secondary" onClick={() => setStep("path")}>
            Back
          </button>
        </>
      )}
    </div>
  );
}
